/**
 * @fileoverview Tracks and logs AI model usage, including token counts and costs. Provides a singleton to record and aggregate usage events. Includes methods to add usage, reset the tracker, and log detailed usage information.
 */

import type { LlmPricing, Usage } from '../ai/llm_types'
import { info_block } from './logging'

export type UsageEvent = {
  name: string
  model: LlmPricing & { name: string }
  usage: Usage
  meta?: Record<string, any>
}

export type CostEvent = {
  name: string
  modelName: string
  cost: number
  meta?: Record<string, any>
}

class UsageTracker {
  usages: UsageEvent[] = []
  costs: CostEvent[] = []

  addUsage(event: UsageEvent) {
    this.usages.push(event)
    this.addCost({
      name: event.name,
      modelName: event.model.name,
      cost:
        event.model.promptTokenCost * event.usage.promptTokens +
        event.model.completionTokenCost * event.usage.completionTokens,
      meta: { ...event.meta, ...event.usage },
    })
  }

  addCost(event: CostEvent) {
    this.costs.push(event)
  }

  reset() {
    this.usages = []
    this.costs = []
  }

  log(name: string = '') {
    if (this.costs.length === 0) {
      return
    }
    const totalCost = this.costs.reduce((acc, c) => acc + c.cost, 0)
    info_block(
      `Usage ${name ? `${name} ` : ''}($${totalCost.toFixed(6)})`,
      this.costs
        .map(
          (u) =>
            `${u.name} \$${u.cost} (${u.modelName}) ${Object.entries(u.meta || {})
              .map(([k, v]) => `${k}=${v}`)
              .join(', ')}`,
        )
        .join('\n'),
    )
  }
}

export const SingleTonUsageTracker = new UsageTracker()

export function trackUsage(
  name: string,
  usage: Usage,
  model: LlmPricing & { name: string },
  meta?: Record<string, any>,
) {
  SingleTonUsageTracker.addUsage({
    name,
    model,
    usage,
    meta,
  })
}

export function trackCost(
  name: string,
  modelName: string,
  cost: number,
  meta?: Record<string, any>,
) {
  SingleTonUsageTracker.addCost({
    name,
    modelName,
    cost,
    meta,
  })
}
